import $Api from '@api'

import { AuthInfo } from '@/api/model/user'
import { appCode } from '@/config'
import {
    initializeActiveTenantSession,
    isTenantSessionRecoveryError
} from '@/utils/login-session'
import { createSingleFlight, requestAuthInfoWithRetry } from '@/utils/auth-request'
import { showReloadFallback } from '@/utils/page-load-recovery'

import { AuthTypes } from './type'

const requestAuthInfo = () => new Promise<AuthInfo>((resolve, reject) => {
    $Api.userService.login().subscribe(resolve, reject)
})
const loadAuthInfo = createSingleFlight(() => requestAuthInfoWithRetry(requestAuthInfo))

export interface AuthState {
    authInfo: AuthInfo | null
    privileges: { [key: string]: any }[]
}

export const AuthModule = {
    state: {
        authInfo: null,
        privileges: []
    } as AuthState,
    mutations: {
        [AuthTypes.UPDATE_AUTH]: (state: AuthState, authInfo: AuthInfo) => {
            state.authInfo = authInfo
        },
        [AuthTypes.UPDATE_PRIVILEGES]: (state: AuthState, privileges: { [key: string]: any }[]) => {
            state.privileges = privileges
        }
    },
    getters: {
        tenantId: (state: AuthState): number | null => {
            if (state.authInfo && state.authInfo.tenantId) {
                return state.authInfo.tenantId
            }
            return null
        },
        tenantLogoUrl: (state: AuthState): string | null => {
            if (state.authInfo && state.authInfo.tenantLogoUrl) {
                return state.authInfo.tenantLogoUrl
            }
            return null
        },
        displayName: (state: AuthState): string | null => {
            if (state.authInfo && state.authInfo.displayName) {
                return state.authInfo.displayName
            }
            return null
        },
        userName: (state: AuthState): string | null => {
            if (state.authInfo && state.authInfo.userName) {
                return state.authInfo.userName
            }
            return null
        },
        tenantName: (state: AuthState): string | null => {
            if (state.authInfo && state.authInfo.tenantName) {
                return state.authInfo.tenantName
            }
            return null
        },
        privileges: (state: AuthState): string[] => {
            return state.privileges.map(item => {
                return item.code
            })
        }
    },
    actions: {
        login({ commit, state }) {
            if (state && state.authInfo && state.authInfo.tenantId) {
                return Promise.resolve(state.authInfo)
            }
            return loadAuthInfo()
                .then((authInfo: AuthInfo) => {
                        initializeActiveTenantSession(authInfo.tenantCode)
                        commit(AuthTypes.UPDATE_AUTH, authInfo)
                        commit(AuthTypes.UPDATE_PRIVILEGES, authInfo.privileges)

                        if (authInfo.privileges.length === 0) {
                            const defaultApp = authInfo.apps[0]

                            if (defaultApp && defaultApp.code) {
                                location.href = '/' + defaultApp.code
                            }
                        } else {
                            const fundAppExist = authInfo.apps.find((item: any) => item.code === appCode)

                            if (fundAppExist) {
                                document.title = fundAppExist.name
                            }
                        }

                        return authInfo
                })
                .catch(error => {
                    if (!isTenantSessionRecoveryError(error)) {
                        showReloadFallback()
                    }
                    return Promise.reject(error)
                })
        }
    }
}
